import { Link } from 'react-router-dom'
import type { ConsoleQueueItem } from '../../lib/api'
import { ageHours, ageLabel, queueItemOverdue } from '../../lib/consoleStatus'
import { cn } from '../../lib/utils'
import { card, h2Class, kicker, when } from '../soc/SocShell'
import { SectionMessage } from './ConsoleShell'

// BRIEF-F1b, Waiting: the queue from /hub/console/queue, grouped into
// "Question for the customer" and "Escalated", each with its age. Older than
// 24 hours is red. Whether the customer was told is the route's word.

const GROUPS: { status: string; title: string; empty: string }[] = [
  {
    status: 'ask_customer',
    title: 'Question for the customer',
    empty: 'No question is waiting on a customer.',
  },
  { status: 'escalated', title: 'Escalated', empty: 'Nothing is escalated.' },
]

function told(item: ConsoleQueueItem): string {
  if (item.owner_told_at) return `Customer told ${when(item.owner_told_at) ?? ''}`.trim()
  if (item.woken_at) return `Customer woken ${when(item.woken_at) ?? ''}`.trim()
  if (item.readout_sent_at) return `Told in the readout ${when(item.readout_sent_at) ?? ''}`.trim()
  if (item.customer_told) return 'Customer told'
  return 'Customer not told yet'
}

function QueueItem({ item, now }: { item: ConsoleQueueItem; now: Date }) {
  const overdue = queueItemOverdue(item, now)
  const age = ageLabel(ageHours(item.opened_at, now))
  return (
    <li
      data-testid={`queue-item-${item.episode_id}`}
      data-overdue={overdue ? 'true' : 'false'}
      className={cn(
        card,
        'flex flex-col gap-1 px-5 py-3.5',
        overdue && 'border-l-[6px] border-l-danger bg-danger-light',
      )}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-x-4">
        <span className="text-[16px]">
          <Link
            to={`/console/business/${encodeURIComponent(item.tenant_id)}`}
            className="font-bold text-navy no-underline hover:underline"
          >
            {item.business}
          </Link>
          {' · '}
          {item.person ?? item.upn ?? 'unknown person'}
        </span>
        <span
          data-testid="queue-age"
          className={cn('text-[14px]', overdue ? 'font-bold text-danger' : 'text-[#6b5a3e]')}
        >
          waiting {age}
        </span>
      </div>
      {item.assessment && <p className="m-0 text-[14px] leading-snug">{item.assessment}</p>}
      <div className="flex flex-wrap gap-x-4 text-[13px] text-[#6b5a3e]">
        <span className={cn(!item.customer_told && 'font-bold text-navy')}>{told(item)}</span>
        {item.owner_not_reached_at && (
          <span>Owner not reached {when(item.owner_not_reached_at)}</span>
        )}
        {item.backup_woken_at && <span>Backup claimed {when(item.backup_woken_at)}</span>}
        {item.shift_kind && item.shift_kind !== 'daily' && <span>{item.shift_kind} shift</span>}
        {item.stale && <span className="font-bold">Older than the open-thread window</span>}
        {item.no_handover && (
          <span className="font-bold text-danger">
            Fault: the first line escalated this and no senior read finished
          </span>
        )}
      </div>
    </li>
  )
}

export function ConsoleWaiting({
  items,
  error,
  now,
}: {
  items: ConsoleQueueItem[] | null
  error: string | null
  now: Date
}) {
  return (
    <section id="waiting" aria-label="Waiting" className="flex flex-col gap-3.5">
      <div>
        <div className={kicker}>What is waiting, and on whom</div>
        <h2 className={h2Class}>Waiting</h2>
      </div>
      {error !== null ? (
        <SectionMessage tone="error">Could not load: {error}</SectionMessage>
      ) : items === null ? (
        <SectionMessage>Loading what is waiting…</SectionMessage>
      ) : items.length === 0 ? (
        <SectionMessage>Nothing is waiting on anyone.</SectionMessage>
      ) : (
        <div className="grid gap-6 md:grid-cols-2">
          {GROUPS.map((g) => {
            const mine = items.filter((i) => i.status === g.status)
            return (
              <div key={g.status} className="flex flex-col gap-2.5" aria-label={g.title} role="group">
                <h3 className="m-0 font-serif text-[20px] font-normal">
                  {g.title} <span className="text-[15px] text-[#6b5a3e]">({mine.length})</span>
                </h3>
                {mine.length === 0 ? (
                  <SectionMessage>{g.empty}</SectionMessage>
                ) : (
                  <ul className="m-0 flex list-none flex-col gap-2.5 p-0">
                    {mine.map((i) => (
                      <QueueItem key={i.episode_id} item={i} now={now} />
                    ))}
                  </ul>
                )}
              </div>
            )
          })}
        </div>
      )}
    </section>
  )
}
